import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import {
  FinishedGoodsTransfer,
  FinishedGoodsTransferInsert,
  FinishedGoodsTransferSearchCommand,
  RollsInOtherTransfersResponse,
  FinishedGoodTransferCuadraje,
} from '../../models/inventory/finished-goods-transfer';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class FinishedGoodsTransferProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/finished-good-transfer';

  public getAll(): Observable<ApiResponse<FinishedGoodsTransfer[]>> {
    return this.httpClient.get<ApiResponse<FinishedGoodsTransfer[]>>(this.baseUrl + '/all');
  }

  public search(
    searchCommand: FinishedGoodsTransferSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<FinishedGoodsTransfer>>> {
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<FinishedGoodsTransfer>>>(
      this.baseUrl + '/search' + queryParams,
      searchCommand,
    );
  }

  public insert(body: FinishedGoodsTransferInsert): Observable<ApiResponse<FinishedGoodsTransfer>> {
    return this.httpClient.post<ApiResponse<FinishedGoodsTransfer>>(this.baseUrl, body);
  }

  public getById(id: string): Observable<ApiResponse<FinishedGoodsTransfer>> {
    return this.httpClient.get<ApiResponse<FinishedGoodsTransfer>>(`${this.baseUrl}/${id}`);
  }

  public getCuadraje(id: string): Observable<ApiResponse<FinishedGoodTransferCuadraje>> {
    return this.httpClient.get<ApiResponse<FinishedGoodTransferCuadraje>>(`${this.baseUrl}/${id}/cuadraje`, {
      headers: getSkipperErrorHeader(true),
    });
  }

  public updateStatus(id: string, status: number, toLocationCode?: string): Observable<ApiResponse<FinishedGoodsTransfer>> {
    const body: { newStatus: number; toLocationCode?: string } = {
      newStatus: status,
    };
    if (toLocationCode) {
      body.toLocationCode = toLocationCode;
    }
    return this.httpClient.patch<ApiResponse<FinishedGoodsTransfer>>(`${this.baseUrl}/${id}/status`, body);
  }

  public addRoll(id: string, rollNumber: string): Observable<ApiResponse<FinishedGoodsTransfer>> {
    return this.httpClient.post<ApiResponse<FinishedGoodsTransfer>>(`${this.baseUrl}/${id}/add-roll/${encodeURIComponent(rollNumber)}`, {});
  }

  public removeRoll(id: string, rollNumber: string): Observable<ApiResponse<FinishedGoodsTransfer>> {
    return this.httpClient.delete<ApiResponse<FinishedGoodsTransfer>>(
      `${this.baseUrl}/${id}/remove-roll/${encodeURIComponent(rollNumber)}`,
    );
  }

  public finishedGoodsTransferReport(id: string): Observable<Blob> {
    const queryParams = `?generatePdf=true`;
    return this.httpClient.get(`${this.baseUrl}/report/${id}${queryParams}`, { responseType: 'blob' });
  }

  public getRollsNumbersInTransfer(rollNumbers: string[]): Observable<ApiResponse<RollsInOtherTransfersResponse>> {
    return this.httpClient.post<ApiResponse<RollsInOtherTransfersResponse>>(`${this.baseUrl}/rolls/in-transfer`, rollNumbers);
  }
}
